/**
 * The weather map's station markers (#327 MR9a): the number a marker draws
 * and the name a screen reader hears for it. Pure, shared by the webapp and
 * (later) mobile; each app renders the marker and puts the name on it.
 *
 * A marker is one station, or a group the backend clustered at this zoom
 * (`count > 1`, `apps/stations/views.py` WeatherStationMapClusteringViewSet):
 * a group's reading is the MEDIAN of its stations' latest readings, and its
 * wind direction their circular mean. Pressing one station opens it;
 * pressing a group zooms in on it. The name says which, and always contains
 * the number the marker draws (WCAG 2.5.3, label in name), so a speech-input
 * user can say what they see.
 *
 * Imports the leaf modules, never the utils barrel (`importCycles.test.ts`).
 */
import type {MessageDescriptor, PrimitiveType} from 'react-intl';
import {messages} from '@smartmixin/i18n';

import type {WeatherMapItem} from '../../api/types';
import {
  convertValueToString,
  formatBearingSpeech,
  getRainUnitLabel,
  getTemperatureUnitLabel,
  getWindUnitLabel,
} from '../Units';
import type {Units} from '../Units';

/** The readings the map's markers can show (the metric switch's values). */
export type StationMarkerMetric = 'T' | 'RR_1H' | 'FXY';

export type StationMarkerIntl = {
  formatMessage: (
    descriptor: MessageDescriptor,
    values?: Record<string, PrimitiveType>,
  ) => string;
};

/**
 * The metric's name, as the map's metric switch names it: "Temperature",
 * "Rain (Last hour)", "Gust".
 */
export const stationMarkerMetricLabel = (
  metric: StationMarkerMetric,
  intl: StationMarkerIntl,
): string => {
  switch (metric) {
    case 'RR_1H':
      return `${intl.formatMessage(messages.metrics.rain)} (${intl.formatMessage(messages.calendar.lastHour)})`;
    case 'FXY':
      return intl.formatMessage(messages.metrics.gust);
    case 'T':
    default:
      return intl.formatMessage(messages.metrics.temperature);
  }
};

/**
 * The number the marker draws, without its unit: the temperature rounded to
 * a whole degree, the hourly rain ("0" when dry), the gust speed. `null` for
 * a calm wind, which draws a crosshair and no number.
 */
export const stationMarkerText = (
  item: WeatherMapItem,
  metric: StationMarkerMetric,
  units: Units,
): string | null => {
  const {T, RR_1H, FXY} = item.properties;
  switch (metric) {
    case 'RR_1H':
      return RR_1H ? convertValueToString(RR_1H, 'Rain', units) : '0';
    case 'FXY':
      return FXY ? convertValueToString(FXY, 'Wind', units) : null;
    case 'T':
    default:
      return convertValueToString(T, 'Temperature', units, 0);
  }
};

/** The reading spoken with its unit (and, for wind, where it blows from). */
const spokenValue = (
  item: WeatherMapItem,
  metric: StationMarkerMetric,
  units: Units,
  intl: StationMarkerIntl,
): string => {
  switch (metric) {
    case 'RR_1H':
      return `${stationMarkerText(item, metric, units)} ${getRainUnitLabel(units)}`;
    case 'FXY': {
      const {FXY, DD} = item.properties;
      const speed = `${convertValueToString(FXY ?? 0, 'Wind', units)} ${getWindUnitLabel(units)}`;
      // A calm wind blows from nowhere.
      const direction = FXY ? formatBearingSpeech(DD, intl) : null;
      return direction
        ? intl.formatMessage(messages.weatherMap.stationMarkerWindValue, {
            speed,
            direction,
          })
        : speed;
    }
    case 'T':
    default:
      return `${stationMarkerText(item, metric, units)} ${getTemperatureUnitLabel(units)}`;
  }
};

/**
 * The marker's accessible name: "Temperature 13 °C, one station", or for a
 * group "Temperature 13 °C, median of 8 stations, zoom in".
 */
export const stationMarkerName = (
  item: WeatherMapItem,
  metric: StationMarkerMetric,
  units: Units,
  intl: StationMarkerIntl,
): string => {
  const values = {
    metric: stationMarkerMetricLabel(metric, intl),
    value: spokenValue(item, metric, units, intl),
  };
  const {count} = item.properties;
  return count > 1
    ? intl.formatMessage(messages.weatherMap.clusterMarkerName, {
        ...values,
        count,
      })
    : intl.formatMessage(messages.weatherMap.stationMarkerName, values);
};
